import type { MouseEvent } from 'react';
import { useMemo, useState } from 'react';
import {
  IconButton,
  Tooltip,
  FormControlLabel,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
  Box,
} from '@mui/material';
import { AutorenewOutlined, PublishedWithChangesOutlined, Share } from '@mui/icons-material';
import { useNotifications } from '@/hooks/use-notifications';
import { useTranslations } from '@/i18n/use-translations.tsx';
import type { EventDto } from '@/types/dtos/events/response/event.dto';
import { useCreateEventModeMutation } from '@/state/mutations/events/use-create-event-mode';
import { useForm, Controller, useWatch } from 'react-hook-form';
import { ServerError } from '@/types/errors/server.error';
import useEventMode from '@/components/events/hooks/use-event-mode';
import { isOrganizeMode } from '@/components/events/helpers/mode.helper';
import { useEventStore } from '@/state/stores/use-event-store';
import { usePermissionsQuery } from '@/state/queries/permissions/use-permissions';

type PublishFormData = {
  publishSessionsInTheEvent: boolean;
  publishFlowsInTheEvent: boolean;
};

export const EventPublish = ({
  event,
  onIndicativeClick,
}: {
  event: EventDto | null;
  onIndicativeClick?: () => void;
}) => {
  const t = useTranslations();
  const notification = useNotifications();
  const [isOpen, setIsOpen] = useState(false);
  const { mutateAsync: createEventModeMutation, isPending: isEventPublishPending } = useCreateEventModeMutation();
  const eventPlan = useEventStore((s) => s.eventPlan);
  const eventOrganize = useEventStore((s) => s.eventOrganize);
  const eventFlowsPlan = useEventStore((s) => s.eventFlowsPlan);
  const eventSessionsPlan = useEventStore((s) => s.eventSessionsPlan);
  const { data: permissions } = usePermissionsQuery();
  const eventMode = useEventMode();

  const eventToUse = eventPlan ?? eventOrganize ?? event;
  const eventId = eventPlan?.id ?? eventOrganize?.id ?? event?.id;
  const canUpdateEvent = !!eventId && !!permissions?.events?.[eventId]?.includes('update');
  const showSessionsOption = canUpdateEvent && !!eventSessionsPlan && eventSessionsPlan.length > 0;
  const showFlowsOption = canUpdateEvent && !!eventFlowsPlan && eventFlowsPlan.length > 0;

  const sessionsUpToDate = useMemo(() => {
    if (!eventSessionsPlan || !showSessionsOption) return true;
    return eventSessionsPlan.every((session) => {
      const organizePublished = session.publishedModes?.find((pm) => pm.mode === 'ORGANIZE');
      return !!organizePublished && organizePublished.version === session.version;
    });
  }, [eventSessionsPlan, showSessionsOption]);

  const flowsUpToDate = useMemo(() => {
    if (!eventFlowsPlan || !showFlowsOption) return true;
    return eventFlowsPlan.every((flow) => {
      const organizePublished = flow.publishedModes?.find((pm) => pm.mode === 'ORGANIZE');
      return !!organizePublished && organizePublished.version === flow.version;
    });
  }, [eventFlowsPlan, showFlowsOption]);

  const publishState = useMemo(() => {
    if (!eventToUse) {
      return {
        isUpToDate: true,
        needsInitialPublish: false,
        needsRepublish: false,
        eventUpToDate: true,
      };
    }
    const organizePublished = eventToUse.publishedModes?.find((pm) => pm.mode === 'ORGANIZE');
    const hasOrganizeChildEvent = !!organizePublished;
    const eventOutOfSync = !!organizePublished && organizePublished.version !== eventToUse.version;

    const sessionsOutOfSync = showSessionsOption && !sessionsUpToDate;
    const flowsOutOfSync = showFlowsOption && !flowsUpToDate;
    const isUpToDate = hasOrganizeChildEvent && !eventOutOfSync && !sessionsOutOfSync && !flowsOutOfSync;
    const needsInitialPublish = !hasOrganizeChildEvent;
    const needsRepublish = !needsInitialPublish && !isUpToDate;

    return {
      isUpToDate,
      needsInitialPublish,
      needsRepublish,
      eventUpToDate: hasOrganizeChildEvent && !eventOutOfSync,
    };
  }, [eventToUse, showSessionsOption, showFlowsOption, sessionsUpToDate, flowsUpToDate]);

  const showPublishModal = !isOrganizeMode(eventMode);
  const isPublishDisabled = isOrganizeMode(eventMode) || publishState.isUpToDate;
  const isIndicativeOnly = isOrganizeMode(eventMode);
  const isButtonDisabled = isPublishDisabled && !isIndicativeOnly;

  const { control, handleSubmit, reset } = useForm<PublishFormData>({
    defaultValues: {
      publishSessionsInTheEvent: false,
      publishFlowsInTheEvent: false,
    },
  });
  const formValues = useWatch({ control });
  const isConfirmDisabled =
    !formValues ||
    !(
      !publishState.eventUpToDate ||
      (formValues.publishSessionsInTheEvent && showSessionsOption && !sessionsUpToDate) ||
      (formValues.publishFlowsInTheEvent && showFlowsOption && !flowsUpToDate)
    );

  if (!eventToUse) return null;

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    if (isIndicativeOnly) {
      onIndicativeClick?.();
      return;
    }
    if (showPublishModal) {
      setIsOpen(true);
    }
  };

  const handleIndicativeClick = (event: MouseEvent<HTMLSpanElement>) => {
    if (!isIndicativeOnly) return;
    event.stopPropagation();
    onIndicativeClick?.();
  };

  const handleCancel = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setIsOpen(false);
    reset();
  };

  const onSubmit = async (data: PublishFormData) => {
    if (!eventId) return;

    const shouldPublishEvent = !publishState.eventUpToDate;
    const shouldPublishSessions = showSessionsOption && !sessionsUpToDate && data.publishSessionsInTheEvent;
    const shouldPublishFlows = showFlowsOption && !flowsUpToDate && data.publishFlowsInTheEvent;
    const shouldSubmit = shouldPublishEvent || shouldPublishSessions || shouldPublishFlows;

    if (!shouldSubmit) {
      setIsOpen(false);
      reset();
      return;
    }

    try {
      await createEventModeMutation({
        id: eventId,
        mode: 'ORGANIZE',
        publishSessionsInTheEvent: shouldPublishSessions,
        publishFlowsInTheEvent: shouldPublishFlows,
      });
      notification.notifySuccess(t('event.notification.publishSuccess', { title: eventToUse.title }));
      setIsOpen(false);
      reset();
    } catch (error) {
      const errorMessage = t('event.notification.publishError');
      const serverErrorMessage = error instanceof ServerError ? error.message : undefined;
      const errorExplanation = serverErrorMessage ? `: ${serverErrorMessage}` : '';
      notification.notifyError(`${errorMessage}${errorExplanation}`);
    }
  };

  const tooltipTitle = isOrganizeMode(eventMode)
    ? publishState.needsRepublish
      ? t('common.unpublishedUpdates')
      : t('common.notPublishedYet')
    : publishState.needsRepublish
      ? t('common.rePublish')
      : publishState.needsInitialPublish
        ? t('common.publish')
        : t('common.upToDate');

  const renderIcon = () => {
    if (publishState.isUpToDate)
      return <PublishedWithChangesOutlined sx={{ color: 'success.main' }} fontSize="small" />;
    if (publishState.needsRepublish) return <AutorenewOutlined color="secondary" fontSize="small" />;
    return <Share fontSize="small" />; // initial publish
  };

  return (
    <>
      <Tooltip title={tooltipTitle}>
        <span style={{ display: 'inline-block' }} onClick={handleIndicativeClick}>
          <IconButton
            aria-label="publish"
            aria-disabled={isIndicativeOnly || undefined}
            color="info"
            size="small"
            disabled={isButtonDisabled}
            onClick={handleClick}
            loading={isEventPublishPending}
            sx={{ width: '24px', height: '24px' }}
          >
            {renderIcon()}
          </IconButton>
        </span>
      </Tooltip>
      <Dialog
        onClick={(e) => e.stopPropagation()}
        open={isOpen}
        onClose={handleCancel}
        aria-labelledby="publish-dialog-title"
        aria-describedby="publish-dialog-text"
      >
        <DialogTitle id="publish-dialog-title">{t('event.publishModal.title')}</DialogTitle>
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogContent>
            <DialogContentText id="publish-dialog-text">
              {t('event.publishModal.text', { title: eventToUse.title })}
            </DialogContentText>
            <Box sx={{ mt: 2 }}>
              <Box sx={{ mb: 1, fontWeight: 'bold' }}>{`${t('common.publish')}:`}</Box>
              {publishState.eventUpToDate ? (
                <FormControlLabel
                  control={
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        width: 42,
                        height: 42,
                        justifyContent: 'center',
                      }}
                    >
                      <PublishedWithChangesOutlined sx={{ color: 'success.main' }} fontSize="small" />
                    </Box>
                  }
                  label="Event"
                  disabled
                  sx={{
                    '&.Mui-disabled': { color: 'text.primary' },
                    'alignItems': 'center',
                    'mb': 0.5,
                    '.MuiSvgIcon-root': { color: 'success.main' },
                  }}
                />
              ) : (
                <FormControlLabel
                  control={<Checkbox checked={true} disabled={true} />}
                  label="Event"
                  sx={{ alignItems: 'center' }}
                />
              )}
              {showSessionsOption ? (
                sessionsUpToDate ? (
                  <FormControlLabel
                    control={
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          width: 42,
                          height: 42,
                          justifyContent: 'center',
                        }}
                      >
                        <PublishedWithChangesOutlined sx={{ color: 'success.main' }} fontSize="small" />
                      </Box>
                    }
                    label={t('event.publishModal.sessionsInTheEvent')}
                    disabled
                    sx={{
                      '&.Mui-disabled': { color: 'text.primary' },
                      'alignItems': 'center',
                      '.MuiSvgIcon-root': { color: 'success.main' },
                    }}
                  />
                ) : (
                  <Controller
                    name="publishSessionsInTheEvent"
                    control={control}
                    render={({ field }) => (
                      <FormControlLabel
                        control={<Checkbox checked={field.value} onChange={field.onChange} />}
                        label={t('event.publishModal.sessionsInTheEvent')}
                        sx={{ alignItems: 'center' }}
                      />
                    )}
                  />
                )
              ) : null}
              {showFlowsOption ? (
                flowsUpToDate ? (
                  <FormControlLabel
                    control={
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          width: 42,
                          height: 42,
                          justifyContent: 'center',
                        }}
                      >
                        <PublishedWithChangesOutlined sx={{ color: 'success.main' }} fontSize="small" />
                      </Box>
                    }
                    label={t('event.publishModal.flowsInTheEvent')}
                    disabled
                    sx={{
                      '&.Mui-disabled': { color: 'text.primary' },
                      'alignItems': 'center',
                      '.MuiSvgIcon-root': { color: 'success.main' },
                    }}
                  />
                ) : (
                  <Controller
                    name="publishFlowsInTheEvent"
                    control={control}
                    render={({ field }) => (
                      <FormControlLabel
                        control={<Checkbox checked={field.value} onChange={field.onChange} />}
                        label={t('event.publishModal.flowsInTheEvent')}
                        sx={{ alignItems: 'center' }}
                      />
                    )}
                  />
                )
              ) : null}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button color="primary" onClick={handleCancel}>
              {t('common.cancel')}
            </Button>
            <Button
              type="submit"
              loading={isEventPublishPending}
              variant="contained"
              autoFocus
              sx={{ m: '0.4rem' }}
              disabled={isConfirmDisabled}
            >
              {t('common.confirm')}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </>
  );
};
